/**
 * Inline Markdown renderer for campaign-authored Alert and Marketing Message bodies.
 *
 * This file is intentionally duplicated byte-for-byte in two places:
 *   - apps/admin/ui/src/app/pages/CampaignManagement/assets/components/inlineMarkdownBody.tsx
 *   - apps/enterprise/ui/src/enterprise/features/homepage/inlineMarkdownBody.tsx
 *
 * Reason: the CMT authoring preview and the supplier-facing render must agree exactly on what a
 * body looks like, and a copy is cheaper than a published package for two consumers. If a third
 * consumer needs this parsing logic, that is the trigger to move it into `packages/` and delete
 * both copies rather than adding a third.
 *
 * Until then, keep the two copies identical. They are checked by two different Prettier configs
 * (the root `.prettierrc` and enterprise-ui's `@c2fo/standard`), so formatting must satisfy both.
 */
import type { ReactElement, ReactNode } from 'react';

type MarkerKind = 'strong' | 'em' | 'strong-em';

interface MarkerDefinition {
  marker: string;
  kind: MarkerKind;
}

const MARKERS: readonly MarkerDefinition[] = [
  { marker: '***', kind: 'strong-em' },
  { marker: '___', kind: 'strong-em' },
  { marker: '**', kind: 'strong' },
  { marker: '__', kind: 'strong' },
  { marker: '*', kind: 'em' },
  { marker: '_', kind: 'em' },
];

const isAlphaNumeric = (character: string | undefined): boolean =>
  character !== undefined && /[\p{L}\p{N}]/u.test(character);

const canOpenMarker = (body: string, index: number, marker: string): boolean => {
  const previousCharacter: string | undefined = body[index - 1];
  const nextCharacter: string | undefined = body[index + marker.length];

  return !isAlphaNumeric(previousCharacter) && nextCharacter !== undefined && !/\s/u.test(nextCharacter);
};

const canCloseMarker = (body: string, index: number, marker: string): boolean => {
  const previousCharacter: string | undefined = body[index - 1];
  const nextCharacter: string | undefined = body[index + marker.length];

  return previousCharacter !== undefined && !/\s/u.test(previousCharacter) && !isAlphaNumeric(nextCharacter);
};

const findClosingMarker = (body: string, startIndex: number, marker: string): number | undefined => {
  let candidateIndex: number = body.indexOf(marker, startIndex);

  while (candidateIndex !== -1) {
    if (canCloseMarker(body, candidateIndex, marker)) {
      return candidateIndex;
    }

    candidateIndex = body.indexOf(marker, candidateIndex + 1);
  }

  return undefined;
};

const wrapContent = (kind: MarkerKind, children: ReactNode[], key: string): ReactElement => {
  if (kind === 'strong-em') {
    return (
      <strong key={key}>
        <em>{children}</em>
      </strong>
    );
  }

  if (kind === 'strong') {
    return <strong key={key}>{children}</strong>;
  }

  return <em key={key}>{children}</em>;
};

const parseInlineMarkdown = (body: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let textStartIndex = 0;
  let currentIndex = 0;

  while (currentIndex < body.length) {
    const markerDefinition: MarkerDefinition | undefined = MARKERS.find(({ marker }) => {
      return body.startsWith(marker, currentIndex) && canOpenMarker(body, currentIndex, marker);
    });

    if (!markerDefinition) {
      currentIndex += 1;
      continue;
    }

    const contentStartIndex: number = currentIndex + markerDefinition.marker.length;
    const closingIndex: number | undefined = findClosingMarker(body, contentStartIndex, markerDefinition.marker);

    if (closingIndex === undefined) {
      currentIndex += markerDefinition.marker.length;
      continue;
    }

    if (textStartIndex < currentIndex) {
      nodes.push(body.slice(textStartIndex, currentIndex));
    }

    const childKeyPrefix = `${keyPrefix}-${currentIndex}`;
    const children: ReactNode[] = parseInlineMarkdown(body.slice(contentStartIndex, closingIndex), childKeyPrefix);
    nodes.push(wrapContent(markerDefinition.kind, children, childKeyPrefix));

    currentIndex = closingIndex + markerDefinition.marker.length;
    textStartIndex = currentIndex;
  }

  if (textStartIndex < body.length) {
    nodes.push(body.slice(textStartIndex));
  }

  return nodes;
};

/**
 * Renders the allowlisted inline formatting supported by campaign-authored Alert and
 * Marketing Message bodies. Every format other than MARKDOWN is treated as plain text.
 */
export const renderInlineMarkdownBody = (body: string, bodyFormat: string): ReactNode => {
  if (bodyFormat !== 'MARKDOWN') {
    return body;
  }

  return parseInlineMarkdown(body, 'inline-markdown');
};
